//!#system	

<template>
	<div>
		<v-layout>
			<v-dialog v-model="isLoading" persistent width="300">
				<v-card color="primary" dark>
					<v-card-text>
						{{loadingPhrase}}
						<v-progress-linear indeterminate color="white" class="mb-0"></v-progress-linear>
					</v-card-text>
				</v-card>
			</v-dialog>
		</v-layout>
		<v-layout child-flex>
			<v-card flat>
				<main-comment-alert 
					:routeInfo="routeInfo"
					message="Outbound API connections to remote servers via HTTPS."
				/>

				<v-alert v-if="errorState" :value="true" color="error" icon="mdi-alert" class="mr-4 ml-4">
					{{ errorMessage }}
				</v-alert>
				<v-card-title primary-title min-width="100%">
					<v-toolbar flat color="white">
						<v-toolbar-title class="primaryTitleElement">
							Web Services
							<span class="totalRecordsDisplay">{{totalRecordsDisplay}}</span>
						</v-toolbar-title>
						<v-spacer></v-spacer>
						<v-tooltip bottom>
							<template v-slot:activator="{ on }">
								<v-btn v-on="on" icon @click="openCreateWindow()">
									<v-icon>mdi-plus</v-icon>
								</v-btn>
							</template>
							<span>Create</span>
						</v-tooltip>
						<v-tooltip bottom>
							<template v-slot:activator="{ on }">
								<v-btn v-on="on" icon @click="refresh" :disabled="isLoadingData">
									<v-icon>mdi-sync</v-icon>
								</v-btn>
							</template>
							<span>Refresh</span>
						</v-tooltip>
						<v-tooltip bottom>
							<template v-slot:activator="{ on }">
								<v-btn v-on="on" icon @click="exportDialog = true" :disabled="isLoadingData">
									<v-icon>mdi-microsoft-excel</v-icon>
								</v-btn>
							</template>
							<span>Export to Excel</span>
						</v-tooltip>
						<v-tooltip bottom>
							<template v-slot:activator="{ on }">
								<v-btn v-on="on" icon :color="hasDefaultHeaders ? '' : 'warning'" @click="headersChooser = true" :disabled="isLoadingData">
									<v-icon>mdi-view-column-outline</v-icon>
								</v-btn>
							</template>
							<span>Choose Headers</span>
						</v-tooltip>
						<v-menu v-if="browseTo.length > 0" bottom left>
							<template v-slot:activator="{ on }">
								<v-btn v-on="on" icon>
									<v-icon>mdi-dots-horizontal</v-icon>
								</v-btn>
							</template>
							<v-list>
								<v-list-item
									v-for="browser in browseTo"
									:key="browser.friendly"
									@click="goToList(browser)"
								>
									<v-list-item-title>{{browser.friendly}}</v-list-item-title>
								</v-list-item>
							</v-list>
						</v-menu>
						<pin-button :routeInfo="routeInfo" />

					</v-toolbar>
				</v-card-title>


				<batch-actions v-if="selected.length > 0" :routeInfo="routeInfo" :selected="selected" @refresh="refresh" />

				<filter-container :routeInfo="routeInfo" :colorVariant="colorVariant" v-model="chipsFilters" :outsideFilters="queryFilters" @update="newFilters" />

				<v-progress-linear :active="isLoadingData" indeterminate></v-progress-linear>

				<v-data-table 
					v-model="selected"
					:headers="selectedHeaders"
					:items="items" 
					class="elevation-0" 
					show-select
					item-key="id"
					:items-per-page="100"
					:footer-props="footerProps"
					:options.sync="options"
					:server-items-length="full_count_pg"
				>
					<template v-slot:item.data-table-select="{ item, isSelected, select }">
						<span v-if="item.updateLock || item.actionLock" class="lock-icon">
							<lock-status size="small" :updateLock="item.updateLock" :actionLock="item.actionLock" />&nbsp;
						</span>
						<v-simple-checkbox :value="isSelected" @input="select($event)" :ripple="false"></v-simple-checkbox>
					</template>
					<template v-slot:item.queueStatus="{ item }">
						<div @click="selectRow(item)" class="clickable">
							<v-icon v-if="item.queueStatus === 'blocked-highPriority'" small color="red" title="Blocked (high priority)">mdi-cancel</v-icon>
							<v-icon v-if="item.queueStatus === 'blocked'" small color="orange" title="Blocked">mdi-cancel</v-icon>
						</div>
					</template>
					
					<template v-slot:item.createdAt="{ item }">
						<div @click="selectRow(item)" class="clickable">
							<span v-if="isToday(item.createdAt)" class="red--text">•</span>
							<span :title="new Date(item.createdAt).toLocaleString()">{{ item.createdAt | dateFormat }}</span>
						</div>
					</template>
					
					<template v-slot:item.updatedAt="{ item }">
						<div @click="selectRow(item)" class="clickable">
							<span v-if="isToday(item.updatedAt)" class="red--text">•</span>
							<span :title="new Date(item.updatedAt).toLocaleString()">{{ item.updatedAt | dateFormat }}</span>
						</div>
					</template>
					
					<template v-slot:item.serviceKey="{ item }">
						<div @click="selectRow(item)" class="clickable">
							<data-formatter v-bind:value="item['serviceKey']" name="serviceKey" type="character"></data-formatter>
						</div>
					</template>
					
					<template v-slot:item.serviceDescription="{ item }">
						<div @click="selectRow(item)" class="clickable">
							<data-formatter v-bind:value="item['serviceDescription']" name="serviceDescription" type="character"></data-formatter>
						</div>
					</template>
					
					<template v-slot:item.connectionHost="{ item }">
						<div @click="selectRow(item)" class="clickable">
							<data-formatter v-bind:value="item['connectionHost']" name="connectionHost" type="character"></data-formatter>
						</div>
					</template>
					
					<template v-slot:item.username="{ item }">
						<div @click="selectRow(item)" class="clickable">
							<data-formatter v-bind:value="item['username']" name="username" type="character"></data-formatter>
						</div>
					</template>
										
					<template v-slot:item.tags="{ item }">
						<div @click="selectRow(item)" class="clickable">
							<data-formatter v-bind:value="item['tags']" name="tags" type="chips"></data-formatter>
						</div>
					</template>

				</v-data-table>
				<v-alert
					v-if="showFiltersRequired"
					class="mx-6 my-6"
					border="bottom"
					colored-border
					type="warning"
					elevation="2"
				>
					When large results are expected, you need to supply some filters for this page to get the information you're looking for.
				</v-alert>
			</v-card>
		</v-layout>
		<v-dialog persistent scrollable max-width="600px" v-model="createWindow" :key="createWindowKey">
			<create @close="closeCreateWindow" />
		</v-dialog>
		<v-dialog persistent max-width="800px" v-model="exportDialog" scrollable>
			<export-dialog :routeInfo="routeInfo" :headers="selectedHeaders" :filters="currentFilters" @close="exportDialog = false" />
		</v-dialog>
		<v-dialog v-model="headersChooser" max-width="600px" scrollable persistent>
			<v-card class="header-chooser">
				<v-card-title>
					<span class="headline">Choose Headers</span>
				</v-card-title>
				<v-divider></v-divider>
				<v-card-text>
					<div class="py-4">
						Note: The order in which you select these headers will be the order they appear in the table. Try the drag and drop feature to move or sort them.
					</div>
					<v-layout child-flex>
						<v-list color="#efefef">
							<v-subheader inset>Available Headers</v-subheader>
							<v-list-item-group multiple v-model="selectedAvailableHeaderIndices">
								<draggable
									:list="availableHeaders"
									group="headerSelection"
								>
									<v-list-item
										class="draggable"
										v-for="header in availableHeaders"
										:key="header.text"
									>
										<v-list-item-content>
											<v-list-item-title v-text="header.text"></v-list-item-title>
										</v-list-item-content>
									</v-list-item>
								</draggable>
							</v-list-item-group>
						</v-list>
						<div class="headerChooserActions align-self-center">
							<v-item-group class="v-btn-toggle v-btn-toggle--group">
								<v-tooltip bottom>
									<template v-slot:activator="{ on }">
										<v-btn v-on="on" icon tile small @click="availableHeaders=headers.map(header => header); selectedHeaders=[];">
											<v-icon small>mdi-chevron-double-left</v-icon>
										</v-btn>
									</template>
									<span>Move all chosen headers to available headers.</span>
								</v-tooltip>
								<v-tooltip bottom>
									<template v-slot:activator="{ on }">
										<v-btn v-on="on" icon tile small @click="moveHeader('left');">
											<v-icon small>mdi-chevron-left</v-icon>
										</v-btn>
									</template>
									<span>Move selected header to available headers.</span>
								</v-tooltip>
								<v-tooltip bottom>
									<template v-slot:activator="{ on }">
										<v-btn v-on="on" icon tile small @click="moveHeader('right');">
											<v-icon small>mdi-chevron-right</v-icon>
										</v-btn>
									</template>
									<span>Move selected header to chosen headers.</span>
								</v-tooltip>
								<v-tooltip bottom>
									<template v-slot:activator="{ on }">
										<v-btn v-on="on" icon tile small @click="selectedHeaders=headers.map(header => header); availableHeaders=[];">
											<v-icon small>mdi-chevron-double-right</v-icon>
										</v-btn>
									</template>
									<span>Move all available headers to chosen headers.</span>
								</v-tooltip>
							</v-item-group>
						</div>
						<v-list color="#efefef">
							<v-subheader inset>Chosen Headers</v-subheader>
							<v-list-item-group multiple v-model="selectedChosenHeaderIndices">
								<draggable
									:list="selectedHeaders"
									group="headerSelection"
								>
									<v-list-item
										class="draggable"
										v-for="header in selectedHeaders"
										:key="header.text"
									>
										<v-list-item-content>
											<v-list-item-title v-text="header.text"></v-list-item-title>
										</v-list-item-content>
									</v-list-item>
								</draggable>
							</v-list-item-group>
						</v-list>
					</v-layout>
				</v-card-text>
				<v-divider></v-divider>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-tooltip bottom>
						<template v-slot:activator="{ on }">
							<v-btn v-on="on" text @click="setDefaultHeaders();">Reset</v-btn>
						</template>
						<span>Reset the headers to its default state.</span>
					</v-tooltip>
					<v-btn text @click="setUserPrefs(); headersChooser = false">Done</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</div>
</template>
<script>
import tools from '@/tools';
import qs from 'qs';
import draggable from 'vuedraggable';
import apiService from '@/apiService';
import PgtUtilMix from '@/PgtUtilMix';
import DataFormatter from '@/components/pieces/DataFormatter';
import LockStatus from '@/components/pieces/LockStatus';
import FilterContainer from '@/components/filters/FilterContainer';
import MainCommentAlert from '@/components/pieces/MainCommentAlert';
import PinButton from '@/components/pieces/PinButton';
import ExportDialog from '@/views/modals/ExportDialog';
import BatchActions from './WebServiceBatchActions';
import Create from './WebServiceCreate';
import ApplicationFileView from '@/components/pieces/ApplicationFileView';

export default {
	components: {
		DataFormatter,
		Create,
		ApplicationFileView,
		FilterContainer,
		BatchActions,
		LockStatus,
		ExportDialog,
		MainCommentAlert,
		PinButton,
		draggable
		
		
	},
	mixins: [PgtUtilMix],
	beforeRouteUpdate(to, from, next) {
		// called when the route that renders this component has changed,
		// but this component is reused in the new route.
		// For example, for a route with dynamic params `/foo/:id`, when we
		// navigate between `/foo/1` and `/foo/2`, the same `Foo` component instance
		// will be reused, and this hook will be called when that happens.
		// has access to `this` component instance.
		//console.log('List:beforeRouteUpdate', to, from);
		return next();
	},
	beforeRouteEnter(to, from, next) {
		// called before the route that renders this component is confirmed.
		// does NOT have access to `this` component instance,
		// because it has not been created yet when this guard is called!
		return next();
	},
	beforeRouteLeave(to, from, next) {
		// called when the route that renders this component is about to
		// be navigated away from.
		// has access to `this` component instance.
		return next();
	},
	data() {
		return {
			chipsFilters: {},
			cached: false,
			pagination: {
				sortBy: 'id'
			},
			isLoading: false,
			loadingTime: 0,
			errorState: false,
			items: [],
			comment: '',
			routeInfo: {
				namespace: 'bpa', 
				title: 'Web Service', 
				name: 'webService', 
				pluralName: 'webServices',
				id: null
			},
			colorVariant: 'deep-purple accent-2',
			browseTo: [],
			fields: [],
			listPath: '',
			selected: [],
			footerProps: {
				showFirstLastPage: false,
				itemsPerPageOptions: [100, 250, 500],
				itemsPerPageText: 'View per page'
			},
			createWindow: false,
			createWindowKey: '',
			showMainCommentBox: true,
			showFilterForm: false,
			headers: [{"text":"Created At","align":"left","value":"createdAt","sortable":true},{"text":"Updated At","align":"left","value":"updatedAt","sortable":true},{"text":"Service Key","align":"left","value":"serviceKey","sortable":true},{"text":"Service Description","align":"left","value":"serviceDescription","sortable":true},{"text":"Connection Host","align":"left","value":"connectionHost","sortable":true},{"text":"Username","align":"left","value":"username","sortable":true},{"text":"Queue Status","align":"left","value":"queueStatus","sortable":true,"sortAs":"blocking.\"highPriority\""},{"text":"Tags","align":"left","value":"tags","sortable":true,"sortAs":"tagged.tags"}],
			defaultHeaders: [{"text":"Service Key","align":"left","value":"serviceKey","sortable":true},{"text":"Service Description","align":"left","value":"serviceDescription","sortable":true},{"text":"Connection Host","align":"left","value":"connectionHost","sortable":true},{"text":"Username","align":"left","value":"username","sortable":true},{"text":"Created At","align":"left","value":"createdAt","sortable":true},{"text":"Tags","align":"left","value":"tags","sortable":true,"sortAs":"tagged.tags"}],
			availableHeaders: [],
			customHeaders: [],
			selectedHeaders: [],
			fetchedHeaders: [],
			selectedAvailableHeaderIndices: [],
			selectedChosenHeaderIndices: [],
			headersChooser: false,
			dragging: false,
			possibleFilters: {},
			currentFilters: {},
			queryFilters: {},
			lastLifecyle: '',
			options: {},
			full_count_pg: 0,
			totalRecordsDisplay: '',
			largeData: false,
			showFiltersRequired: false,
			exportDialog: false,
			loadingSavedFilter: false
		};
	},
	computed: {
		title() {
			return this.routeInfo.title;
		},
		isLoadingData() {
			return this.loadingTime > 0
		},
		loadingPhrase() {
			return tools.loadingText()
		},
		searchIcon() {
			return this.showFilterForm ? 'mdi-magnify-minus' : 'mdi-magnify';
		},
		hasDefaultHeaders() {
			if (this.selectedHeaders.length === 0) return false;
			if (this.selectedHeaders.length !== this.defaultHeaders.length) return false;

			let hasDefaultHeaders = true;

			for (let i = 0; i < this.defaultHeaders.length; i++) {
				if (!this.selectedHeaders[i] || this.defaultHeaders[i].text !== this.selectedHeaders[i].text) {
					hasDefaultHeaders = false;
					break;
				}
			}

			return hasDefaultHeaders;
		}
	},
	watch: {
		options: {
			handler(newOptions, oldOptions) {
				const oldOptionsIsEmpty = tools.isEmpty(oldOptions);
				const sortChangeCol = (newOptions.sortBy ? newOptions.sortBy[0] : '') !== ( oldOptions.sortBy ? oldOptions.sortBy[0] : '');
				const sortChangeDir = (newOptions.sortDesc ? newOptions.sortDesc[0] : '') !== (oldOptions.sortDesc ? oldOptions.sortDesc[0] : '');
				const pageChange = newOptions.page !== oldOptions.page;
				const sortChange = sortChangeCol || sortChangeDir || pageChange;
				if (['deactivated', 'created'].includes(this.lastLifecyle)) return;
				if (this.loadingSavedFilter && (!sortChange || oldOptionsIsEmpty)) {
					return;
				}
				if (this.options.page > 1) {
					this.isLoading = true;
					this.loadingTime = 1;
				}
				this.handleParams();
			}
		},
		deep: true
	},
	filters: {
		dateFormat(val) {
			if (navigator && Array.isArray(navigator.languages) && navigator.languages[0] === 'en-US') {
				const [datePart, timePart] = (val ? (new Date(val)).toLocaleString() : '--').split(',');
				const datePartToday = (val ? (new Date()).toLocaleString() : '--').split(',')[0];
				if (datePart && timePart && datePartToday && datePart === datePartToday) {
					return 'Today,' + timePart;
				}
			}
			return val ? (new Date(val)).toLocaleString() : '--';
		}
	},
	methods: {
		goToList(browser) {
			tools.goToList(browser);
		},
		getSafe(fn, defaultVal) {
			try {
				return fn();
			} catch (e) {
				return defaultVal;
			}
		},
		newFilters(filters) {
			console.log('got new filter', filters);
			this.currentFilters = filters;
			this.options.page = 1; // Reset page when updating filters
			this.refresh();
		},
		lockColor(updateLock, actionLock) {
			if (updateLock && actionLock) return 'red accent-3';
			if (updateLock && !actionLock) return 'light-blue lighten-2';
			if (!updateLock && actionLock) return 'teal lighten-1';
			return 'blue-grey lighten-5';
		},
		lockTitle(updateLock, actionLock) {
			if (updateLock && actionLock) return 'Locked from updates and actions';
			if (updateLock && !actionLock) return 'Locked from updates';
			if (!updateLock && actionLock) return 'Locked from actions';
			return 'Not locked';
		},
		toggleAll() {
			console.log('toggleAll', this.selected);
			if (this.selected.length) this.selected = [];
			else this.selected = this.items.slice();
		},
		changeSort (column) {
			if (this.pagination.sortBy === column) {
				this.pagination.descending = !this.pagination.descending
			} else {
				this.pagination.sortBy = column
				this.pagination.descending = false
			}
		},
		isToday(val) {
			const [datePart, timePart] = (val ? (new Date(val)).toLocaleString() : '--').split(',');
			const datePartToday = (val ? (new Date()).toLocaleString() : '--').split(',')[0];
			if (datePart && timePart && datePartToday && datePart === datePartToday) {
				return true
			}
			return false;
		},
		updateRouteInfo(parsedRouteInfo) {
			this.routeInfo = { ...this.routeInfo, ...parsedRouteInfo };
		},
		refresh() {
			if (this.$refs.tools && this.$refs.tools.loadAllData) this.$refs.tools.loadAllData();
			this.getNeededData();
		},
		getPossibleFilters() {
			apiService.possibleFilters(this.routeInfo, (err, response) => {
				if (err) return this.errorState = true;
				this.possibleFilters = response;
				tools.filtersFormat(this.possibleFilters);
			});
		},
		handleSortChanges(sortBy, sortDesc) {
			let sortObj = {};
			if (!Array.isArray(sortBy) || sortBy.length <= 0) return sortObj;
			if (!Array.isArray(sortDesc) || sortDesc.length <= 0) return sortObj;
			let sortFieldStr = sortBy[0];
			let sortDescBool = sortDesc[0];
			let alias = 'main';
			this.headers.forEach(header => {
				if (sortFieldStr === header.value) {
					sortObj._orderBy_ = alias + '.' + header.value;
					sortObj._orderDirection_ = !sortDescBool ? 'ASC' : 'DESC';
					if (header.sortAs) {
						sortObj._orderBy_ = header.sortAs;
					}
				}
			});
			return sortObj;
		},
		filtersAreEmpty() {
			if (tools.isEmpty(this.currentFilters)) return true;
			if (typeof this.currentFilters === 'object' && Object.keys(this.currentFilters).length === 1 && Object.keys(this.currentFilters)[0] === '*deep*') {
				if (Array.isArray(this.currentFilters['*deep*']) && this.currentFilters['*deep*'].length === 0) return true;
			}
			return false;
		},
		getListData() {
			if (this.largeData && this.filtersAreEmpty(this.currentFilters)) {
				this.showFiltersRequired = true;
				return;
			} else {
				this.showFiltersRequired = false;
			}
			this.getCustomHeaders(); // just run async
			this.loadingTime = 1;
			this.selected = [];
			let elapser = tools.elapsed().start();
			setTimeout(() => {
				let seconds = elapser.end();
				if (seconds >= 1 && this.loadingTime >= 1) {
					this.isLoading = true;
					this.loadingTime = seconds;
				}
			}, 1100);
			const { sortBy, sortDesc, page, itemsPerPage } = this.options;
			const limit = itemsPerPage > 100 ? itemsPerPage : 100;
			const offest = (page - 1) * itemsPerPage;
			const sortObj = this.handleSortChanges(sortBy, sortDesc);
			if (sortObj._orderBy_) this.currentFilters._orderBy_ = sortObj._orderBy_;
			if (sortObj._orderDirection_) this.currentFilters._orderDirection_ = sortObj._orderDirection_;
			this.currentFilters._limit_ = limit;
			this.currentFilters._offset_ = offest;
			apiService.list({ ...this.routeInfo, filters: this.currentFilters }, (err, response, meta) => {
				if (err) {
					this.errorMessage = '';
					response.errors.forEach(error => {
						this.errorMessage += error.message + ' ';
					});
					this.errorState = true;
					this.isLoading = false;
					this.loadingTime = 0;
					return;
				}
				if (response.length > 0) {
					this.full_count_pg = +response[0].full_count_pg;
					if (this.full_count_pg > 0) this.totalRecordsDisplay = '| ' + this.full_count_pg.toLocaleString();
				} else {
					this.totalRecordsDisplay = '| 0';
				}
				this.errorState = false;
				this.errorMessage = '';
				this.items = response;
				this.isLoading = false; // move this to apiService
				this.loadingTime = 0;
				this.fields = meta.listMeta.fields;
				this.comment = meta.comment;
				this.showMainCommentBox = true;
				this.getUserPrefs();
			});
		},
		getUserPrefs() {
			const prefkey = tools.prefKey(this.routeInfo, 'tableHeaders');
			const selectedHeaders = [];
			let pref = this.$store.getters['authentication/pref'](prefkey);
			if (Array.isArray(pref)) {
				this.availableHeaders = [];
				this.headers.map(header => {
					if (pref.includes(header.text)) {
						selectedHeaders[pref.indexOf(header.text)] = header;
					} else {
						this.availableHeaders.push(header);
					}
				});
				this.selectedHeaders = selectedHeaders.filter(v => !!v);
				this.fetchedHeaders = pref;
			}
		},
		setUserPrefs() {
			const prefkey = tools.prefKey(this.routeInfo, 'tableHeaders');
			const prefsToMerge = {};
			prefsToMerge[prefkey] = this.selectedHeaders.map(header => header.text);

			if (!tools.arrayEquals(prefsToMerge[prefkey], this.fetchedHeaders)) {
				this.$store.dispatch('authentication/setPrefs', { prefsToMerge, callback: () => {
					this.getUserPrefs();
				}});
			}
		},
		getCustomHeaders() {
			if (this.customHeaders.fetched) return; // make them refresh their browser to get these
			apiService.customHeaders({ ...this.routeInfo }, (err, customHeaders) => {
				if (!err) this.customHeaders = customHeaders;
				this.customHeaders.fetched = true;
				this.customHeaders.forEach(customHeader => {
					this.headers.push(customHeader);
				});
			});
		},
		setDefaultHeaders() {
			let selectedHeadersText = [];
			this.availableHeaders = [];
			this.selectedHeaders = this.defaultHeaders.map(header => header);
			selectedHeadersText = this.selectedHeaders.map(header => header.text);

			this.headers.map(header => {
				if (!selectedHeadersText.includes(header.text)) {
					this.availableHeaders.push(header);
				}
			});
		},
		moveHeader(direction) {
			if (direction === 'left' && this.selectedChosenHeaderIndices.length > 0) {
				this.selectedChosenHeaderIndices.map(index => {
					this.availableHeaders.push(this.selectedHeaders[index]);
					this.selectedHeaders[index] = null;
				});
				this.selectedHeaders = this.selectedHeaders.filter(header => header !== null);
				this.selectedChosenHeaderIndices = [];
			} else if (direction === 'right' && this.selectedAvailableHeaderIndices.length > 0) {
				this.selectedAvailableHeaderIndices.map(index => {
					this.selectedHeaders.push(this.availableHeaders[index]);
					this.availableHeaders[index] = null
				});
				this.availableHeaders = this.availableHeaders.filter(header => header !== null);
				this.selectedAvailableHeaderIndices = [];
			}
		},
		getNeededData() {
			this.handleParams();
			this.getPossibleFilters();
		},
		dismissMainCommentBox(v) {
			this.showMainCommentBox = v;
			// TODO: Add a dialog where they can choose if they never want to see this box again
		},
		closeCreateWindow() {
			this.createWindow = false;
			this.getListData();
		},
		openCreateWindow() {
			this.createWindowKey = [...Array(10)].map(() => (~~(Math.random() * 36)).toString(36)).join('');
			this.createWindow = true;
		},
		toggleFilterForm() {
			this.showFilterForm = !this.showFilterForm;
		},
		selectRow(rowData) {
			//alert(rowData);
			tools.goToDetail(this.routeInfo, rowData.id, { basePath: 'admin/' });
		}
	},
	created() {
		this.lastLifecyle = 'created';
		this.setDefaultHeaders();
		console.log('('+this.lastLifecyle+') List for ', this.routeInfo.namespace, this.routeInfo.name);
	},
	mounted() {
		this.handleParams = this.debounce(() => {
			this.getListData();
		}, 50);
	},
	activated() {
		this.lastLifecyle = 'activated';
		let getData = false;
		let forceRefresh = false;
		if (this.$route.query && Object.keys(this.$route.query).length > 0) {
			let qu = window.location.search.split('?')[1];
			let fil = qs.parse(qu);
			if (fil._saved_) {
				// Special - just pass the filter code to the FilterContainer,
				// and don't treat these params as search params.
				this.queryFilters = fil;
				this.loadingSavedFilter = true;
				return; // the FilterContainer will emit the event to get the data
			} else if (fil._refresh_) {
				// An object was just deleted, and the user is being navigated back to the list page
				// Force a refresh, so that the deleted object does not appear in the list
				getData = true;
				forceRefresh = true;
			} else {
				this.loadingSavedFilter = false;
				this.currentFilters = fil;
				this.queryFilters = fil;
			}
			getData = true;
			// ^^ this logic causes the query to reload every time
			// even on back-buttons
			// which could be good, like if you're 'working queues'
			// it would be nice to have the list reload every time you
			// hit the back button, so that an item you worked (removed 
			// from the queue) would then disappear. Think about this...
			// Another option, cache the $route.query, and if it does
			// not change, then do not getData - but - if the query-time
			// is short, do get the data again.
		}
		this.loadingSavedFilter = false;
		console.log('('+this.lastLifecyle+') List for ', this.routeInfo.namespace, this.routeInfo.name);
		if (this.cached === false) {
			this.cached = Date.now();
			getData = true;
		} else {
			// time diff since cache, in seconds
			const diff = (Date.now() - this.cached) / 1000
			// if cache is too old
			if (diff > (60 * 2)) {
				this.cached = false;
				getData = true;
			}
		}
		if (getData) {
			this.getNeededData();
			if (forceRefresh) {
				this.getListData();
			}
		}
	},
	deactivated() {
		this.lastLifecyle = 'deactivated';
	}
};

/**
export default {
   name: 'myComponent',
   beforeCreated () {},
   created () {},
   mounted () {},
   beforeDestroy () {},
   destroy () {}
}
 */
</script>
<style scoped>
.clickable {
	cursor: pointer;
}
.draggable {
	cursor: move; /* fallback if grab cursor is unsupported */
	cursor: grab;
	cursor: -moz-grab;
	cursor: -webkit-grab;
}
.draggable:active {
	cursor: grabbing;
	cursor: -moz-grabbing;
	cursor: -webkit-grabbing;
}
.v-datatable /deep/ thead th.custom-align-left {
	text-align: left;
}
.primaryTitleElement {
	margin-left: -16px;
}
.layout > .v-card > .v-card__title {
	padding-top: 0px !important;
	padding-bottom: 0px !important;
}

.v-data-table /deep/ thead .v-icon {
	padding: 0 3px 0 3px;
}

.totalRecordsDisplay {
	font-size: 80%;
	color: gray;
}

.headerChooserActions {
	text-align: center;
}
.headerChooserActions .v-btn-toggle {
	flex-direction: column;
}
.header-chooser .v-list {
	max-height: 400px;
	overflow-y: auto;
}
.header-chooser .v-list > .v-list-item-group,
.header-chooser .v-list > .v-list-item-group > div {
	height: 336px;
}
.header-chooser .v-card__text {
	overflow-y: hidden;
}
.header-chooser .headline {
	font-family: 'Poppins' !important;
}
.lock-icon {
	position: absolute;
	left: 40px;
}
</style>